import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { UploadFileService } from '../upload-file.service';
import { FileUpload } from '../fileupload';
import { FormGroup, Validators, FormControl } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { finalize } from 'rxjs/operators';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { NotificationService } from 'src/app/shared/services/notification.service';


@Component({
  selector: 'app-form-upload',
  templateUrl: './form-upload.component.html',
  styleUrls: ['./form-upload.component.css'],
  inputs: ['idBot', 'isNew', 'uploadfilePath', 'imagelink', 'ShowAlt', 'infoImage', 'idCatalog', 'for_product_preview', 'idUser', 'imageRatio', 'uploadType', 'botType', 'editMode']
})
export class FormUploadComponent implements OnInit {
  for_product_preview
  selectedFiles!: FileList | undefined;
  currentFileUpload!: FileUpload;
  progress: { percentage: number } = { percentage: 0 };
  botImageURL!: boolean;
  download!: boolean;
  downloadURL: any;
  uploadfilePath;
  infoImage;
  link: any;
  image;
  editMode = true;
  imagelink: any;
  idBot;
  ShowAlt;
  showURL = false
  showALTValue = false
  idUser: any
  imageRatio!: boolean
  /** When `'profileImage'`, applies bot avatar rules (QW5): validate size/dimensions, square crop, resize to 256×256. */
  uploadType!: string;
  @Output() urlImageCard = new EventEmitter();
  @Output() showURLInput = new EventEmitter();
  @Output() showALTInput = new EventEmitter();
  @Output() imageRatioType = new EventEmitter();
  cities: any[] | undefined;
  selectedCity: any;
  isUploading: boolean = false;

  constructor(
    private storage: AngularFireStorage,
    private messageService: MessageService,
    private notificationService: NotificationService,
  ) { }

  ngOnInit() {

    this.cities = [
      { name: 'New York', code: 'NY' },
      { name: 'Rome', code: 'RM' },
      { name: 'London', code: 'LDN' },
      { name: 'Istanbul', code: 'IST' },
      { name: 'Paris', code: 'PRS' }
    ];
  }

  private notify(
    kind: 'success' | 'error' | 'warning',
    summary: string,
    detail?: string,
  ): void {
    this.notificationService.show(kind, detail ? `${summary} ${detail}` : summary);
    try {
      this.messageService.add({
        severity: kind === 'warning' ? 'warn' : kind === 'success' ? 'success' : 'error',
        summary,
        detail,
      });
    } catch {
      /* MessageService is not provided on every page */
    }
  }

  URLInput(event?: Event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.showURL = !this.showURL
    this.showURLInput.emit(this.showURL);
    this.urlImageCard.emit(this.imagelink);
  }

  ALTInput() {
    this.showALTValue = !this.showALTValue
    this.showALTInput.emit(this.showALTValue);
  }


  selectFile(event: Event) {
    const input = event.target as HTMLInputElement;
    this.selectedFiles = input.files ?? undefined;
    void this.upload().finally(() => {
      input.value = '';
    });
  }

  private normalizeAvatarFileName(original: string): string {
    const base = original.replace(/\.[^/.]+$/, '').replace(/[^\w\-]+/g, '_') || 'bot-avatar';
    return `${base}.jpg`;
  }

  private isAcceptedAvatarMime(type: string): boolean {
    return /^image\/(jpeg|jpg|png)$/i.test((type || '').trim());
  }

  /** QW5: reject &gt; 500 KB or sides &gt; 1024 px; center-crop square and resize to 256×256 JPEG. */
  private async processBotAvatarForUpload(file: File): Promise<File | null> {
    const MAX_BYTES_BEFORE = 500 * 1024;
    const MAX_SIDE_PX = 1024;
    const OUT = 256;
    const TARGET_MAX_BYTES = 100 * 1024;

    const extOk = /\.(jpe?g|png)$/i.test(file.name);
    if (!this.isAcceptedAvatarMime(file.type) && !extOk) {
      this.notify('warning', 'Unsupported format', 'Please upload a PNG or JPEG image.');
      return null;
    }

    if (file.size > MAX_BYTES_BEFORE) {
      this.notify('warning', 'File too large', 'Maximum size is 500 KB.');
      return null;
    }

    let bitmap: ImageBitmap;
    try {
      bitmap = await createImageBitmap(file);
    } catch {
      this.notify('warning', 'Invalid image', 'Could not read this image file.');
      return null;
    }

    const w = bitmap.width;
    const h = bitmap.height;
    if (w > MAX_SIDE_PX || h > MAX_SIDE_PX) {
      bitmap.close();
      this.notify('warning', 'Image too large', 'Maximum dimensions are 1024×1024 px.');
      return null;
    }

    const side = Math.min(w, h);
    const sx = (w - side) / 2;
    const sy = (h - side) / 2;

    const canvas = document.createElement('canvas');
    canvas.width = OUT;
    canvas.height = OUT;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      this.notify('error', 'Processing failed', 'Your browser could not process this image.');
      return null;
    }
    ctx.drawImage(bitmap, sx, sy, side, side, 0, 0, OUT, OUT);
    bitmap.close();

    const qualities = [0.92, 0.85, 0.78, 0.72, 0.65, 0.58];
    let blob: Blob | null = null;
    for (const q of qualities) {
      blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/jpeg', q)
      );
      if (blob && blob.size <= TARGET_MAX_BYTES) {
        break;
      }
    }
    if (!blob) {
      this.notify('error', 'Processing failed', 'Could not optimize image.');
      return null;
    }

    const name = this.normalizeAvatarFileName(file.name);
    return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
  }

  async upload(): Promise<void> {
    let file = this.selectedFiles?.item(0) ?? undefined;
    this.selectedFiles = undefined;

    if (!file) {
      return;
    }

    if (this.uploadType === 'profileImage') {
      this.botImageURL = false;
      this.download = true;
      this.isUploading = true;
      const processed = await this.processBotAvatarForUpload(file);
      if (!processed) {
        this.isUploading = false;
        this.download = false;
        return;
      }
      file = processed;
    }

    this.currentFileUpload = new FileUpload(file);

    this.botImageURL = false;
    this.download = true;
    this.isUploading = true;

    const basePath = String(this.uploadfilePath || '')
      .replace(/\/{2,}/g, '/')
      .replace(/\/+$/, '');
    if (!basePath) {
      this.isUploading = false;
      this.download = false;
      this.notify('error', 'Upload failed', 'Reload the page and try again.');
      return;
    }
    const filePath = `${basePath}/${Date.now()}_${file.name}`;
    const fileRef = this.storage.ref(filePath);

    // Add metadata to help with CORS and file handling
    const metadata = {
      contentType: file.type || 'image/jpeg',
      customMetadata: {
        uploadedBy: this.idUser || 'unknown',
        uploadedAt: new Date().toISOString()
      }
    };

    const task = this.storage.upload(filePath, file, metadata);

    task.percentageChanges().subscribe(percentage => {
      this.progress.percentage = Math.round(percentage ? percentage : 0);
    });
    task.snapshotChanges().pipe(
      finalize(() => {
        fileRef.getDownloadURL().subscribe({
          next: (url) => {
            this.imagelink = url;
            this.isUploading = false;
            if (this.imagelink) {
              this.urlImageCard.emit(this.imagelink);
              this.notify('success', 'Image updated successfully!');
            }
          },
          error: (error) => {
            this.notificationService.showError('Error getting download URL:');
            this.isUploading = false;
            this.notify(
              'error',
              'Error uploading file',
              error.message || 'Please check Firebase Storage CORS configuration',
            );
            this.download = false;
          }
        });
      })
    ).subscribe({
      error: (error) => {
        this.notificationService.showError('Error during upload:');
        this.isUploading = false;
        this.notify(
          'error',
          'Upload failed',
          error.message || 'CORS error: Please configure Firebase Storage CORS rules',
        );
        this.download = false;
      }
    });
  }


  changeImageRatio(event) {
    this.imageRatioType.emit(event.value);
  }
}
